import { fabric } from 'fabric';
import CollaborationBehaviors from './CollaborationBehaviors';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import ShapeBase from '../shapes/ShapeBase';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import LockBehaviors from './LockBehaviors';
import type { IPathOptions, Point } from 'fabric/fabric-impl';
import CommentableBehaviors from './CommentableBehaviors';
import StyleCopyPasteBehaviors from './StyleCopyPasteBehaviors';
import { type RecordValue } from '../collaboration/DataStoreRecordBehaviors';

const HIGHLIGHTER_ALPHA = 0.4;

// A hand drawn line generated by the Pen or Highlighter styluses.
interface Path
  extends fabric.Path,
    StyleCopyPasteBehaviors,
    LockBehaviors,
    ReadOnlyBehaviors,
    CollaborationBehaviors,
    FrameMemberBehaviors,
    CommentableBehaviors {}

@LockBehaviors
@ReadOnlyBehaviors
@CollaborationBehaviors
@FrameMemberBehaviors
@CommentableBehaviors
@StyleCopyPasteBehaviors
class Path extends fabric.Path {
  strokeLineCap = 'round';
  pathType: string;

  snapAngle = 45;
  snapThreshold: number | undefined = 5;
  borderOpacityWhenMoving = 0.1;

  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = [
    'z',
    'path',
    'left',
    'top',
    'width',
    'height',
    'angle',
    'scaleX',
    'scaleY',
    'fill',
    'stroke',
    'strokeWidth',
    'pathType',
    'container',
  ];

  constructor(
    path?: string | Point[],
    opts?: IPathOptions & { pathType?: string; locked?: boolean }
  ) {
    const { pathType, ...data } = opts ?? {};
    data.fill = undefined;
    super(path, data);
    this.pathType = pathType ?? 'path';
    this.controls.mtr = ShapeBase.rotateControl();
    this.initializeFrameMember();
    this.initializeLockBehaviors(data.locked ?? false);
  }

  allowedStyleCopyPasteFields(): string[] {
    return ['stroke'];
  }

  set(key: any, value?: RecordValue) {
    if (this.pathType === 'highlighter' && key === 'stroke' && value) {
      const color = new fabric.Color(value as string);
      color.setAlpha(HIGHLIGHTER_ALPHA);
      value = color.toRgba();
    }

    return super.set(key, value);
  }
}

// Override the default path so that the pencil brush creates our paths.
fabric.Path = Path;

export default Path;
